import React from "react";
import { AlertTriangle, RefreshCw, Home, ChevronDown, Zap } from "lucide-react";

// ─── FALLBACK UI ──────────────────────────────────────────────────────────────
function ErrorFallback({ error, errorInfo, onReset, pageName }) {
  const [showDetails, setShowDetails] = React.useState(false);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="bg-white border border-[#E5E1DB] rounded-2xl shadow-sm max-w-lg w-full overflow-hidden">
        {/* Red top bar */}
        <div className="h-1.5 bg-gradient-to-r from-[#C2624E] to-[#E8956D]" />

        <div className="p-8">
          {/* Icon + heading */}
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-[#FBEAE7] rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={22} className="text-[#C2624E]" />
            </div>
            <div>
              <h2 className="text-lg font-extrabold font-[Manrope] text-[#3A322C]">
                Something went wrong
              </h2>
              <p className="text-sm text-[#8A7E73] mt-1">
                {pageName
                  ? `The "${pageName}" page crashed unexpectedly.`
                  : "A page crashed unexpectedly."}
                {" "}Your data is safe — this is a display error only.
              </p>
            </div>
          </div>

          {/* What you can do */}
          <div className="bg-[#F4F3F0] rounded-xl p-4 mb-5 space-y-2">
            <p className="text-xs font-bold uppercase text-[#8A7E73] mb-2">What you can do</p>
            <div className="flex items-start gap-2 text-sm text-[#5C5147]">
              <span className="text-[#637C64] font-bold mt-0.5">1.</span>
              <span>Click <strong>Try Again</strong> — most errors resolve on reload</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-[#5C5147]">
              <span className="text-[#637C64] font-bold mt-0.5">2.</span>
              <span>Use <strong>Reports & AI → Smart Fix</strong> to scan for system issues</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-[#5C5147]">
              <span className="text-[#637C64] font-bold mt-0.5">3.</span>
              <span>If it keeps happening, note the error below and contact support</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 mb-4">
            <button
              onClick={onReset}
              className="flex-1 flex items-center justify-center gap-2 bg-[#E8956D] text-[#3A322C] rounded-xl py-2.5 font-bold text-sm hover:bg-[#D48661] transition-colors"
            >
              <RefreshCw size={15} /> Try Again
            </button>
            <button
              onClick={() => window.location.href = "/"}
              className="flex-1 flex items-center justify-center gap-2 border border-[#E5E1DB] text-[#5C5147] rounded-xl py-2.5 font-bold text-sm hover:bg-[#F4F3F0] transition-colors"
            >
              <Home size={15} /> Go to Dashboard
            </button>
          </div>

          {/* Smart Fix hint */}
          <div className="flex items-center gap-2 bg-[#5C5147]/5 border border-[#5C5147]/10 rounded-xl px-4 py-3 mb-4">
            <Zap size={14} className="text-[#E8956D] flex-shrink-0" />
            <p className="text-xs text-[#5C5147]">
              Run <strong>Reports & AI → Smart Fix → System Logs</strong> to analyze this error automatically with AI
            </p>
          </div>

          {/* Error details (collapsible) */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1.5 text-xs text-[#8A7E73] hover:text-[#5C5147] w-full"
          >
            <ChevronDown
              size={13}
              className={`transition-transform ${showDetails ? "" : "-rotate-90"}`}
            />
            {showDetails ? "Hide" : "Show"} error details
          </button>

          {showDetails && (
            <div className="mt-3 bg-[#1E1B18] rounded-xl p-4 overflow-auto max-h-40">
              <p className="text-xs text-[#FF6B6B] font-mono font-bold mb-1">
                {error?.name}: {error?.message}
              </p>
              {errorInfo?.componentStack && (
                <pre className="text-[10px] text-white/50 font-mono whitespace-pre-wrap mt-2">
                  {errorInfo.componentStack.trim().split("\n").slice(0, 8).join("\n")}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ERROR BOUNDARY CLASS ─────────────────────────────────────────────────────
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log to console for debugging
    console.error("[Manna Trust Error Boundary]", error, errorInfo);
  }

  reset() {
    this.setState({ hasError: false, error: null, errorInfo: null });
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.reset}
          pageName={this.props.pageName}
        />
      );
    }
    return this.props.children;
  }
}

// ─── FULL-SCREEN ERROR BOUNDARY (for top-level App crashes) ──────────────────
export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[App Error Boundary]", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 bg-[#FBEAE7] rounded-2xl flex items-center justify-center mx-auto mb-5">
              <AlertTriangle size={36} className="text-[#C2624E]" />
            </div>
            <h1 className="text-2xl font-extrabold font-[Manrope] text-[#3A322C] mb-2">
              Manna Trust — App Error
            </h1>
            <p className="text-[#8A7E73] text-sm mb-6">
              The application encountered an unexpected error. Please reload the page.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="flex items-center gap-2 bg-[#E8956D] text-[#3A322C] rounded-xl px-6 py-3 font-bold hover:bg-[#D48661]"
              >
                <RefreshCw size={16} /> Reload App
              </button>
              <button
                onClick={() => { window.location.href = "/"; }}
                className="flex items-center gap-2 border border-[#E5E1DB] text-[#5C5147] rounded-xl px-6 py-3 font-bold hover:bg-[#F4F3F0]"
              >
                <Home size={16} /> Go Home
              </button>
            </div>
            <details className="mt-6 text-left">
              <summary className="text-xs text-[#8A7E73] cursor-pointer hover:text-[#5C5147]">Error details</summary>
              <pre className="mt-2 text-xs text-[#C2624E] bg-[#FBEAE7] rounded-lg p-3 overflow-auto max-h-32">
                {this.state.error?.toString()}
              </pre>
            </details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
